import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../UI/Button/Button";
import LoadingSpinner from "../../UI/LoadingSpinner/LoadingSpinner";
import styles from "./BillingHistory.module.css";
import billingHistoryService from "../../../services/billing-history-service.js";
import invoiceService from "../../../services/invoice-service.js";
import { useAuth } from "../../../context/authContext";
import { safeFormatDate } from "../../../utils/dateUtils.js";

export default function BillingHistory() {
  const { t } = useTranslation("common");
  const [billingHistory, setBillingHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const { user, isAuthenticated } = useAuth();

  const fetchBillingHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const billingResponse = await billingHistoryService.getBillingHistory(
        user.email,
      );

      if (billingResponse.success) {
        const allRecords = billingResponse.data || [];

        const subscriptionRecords = allRecords.filter(
          (record) => record.recordType === "subscription",
        );
        const paymentRecords = allRecords.filter(
          (record) => record.recordType === "payment",
        );

        setBillingHistory(subscriptionRecords);
        setPaymentHistory(paymentRecords);
      } else {
        setError("Failed to load billing history");
      }
    } catch (err) {
      console.error("Error fetching billing history:", err);
      setError("Failed to load billing history");
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      fetchBillingHistory();
    }
  }, [isAuthenticated, user?.email, fetchBillingHistory]);

  const formatAmount = (amount, currency = "EUR") => {
    if (amount === null || amount === undefined || amount === "") return "N/A";
    try {
      const numAmount = Number(amount);
      if (isNaN(numAmount)) return "Invalid Amount";

      let symbol;
      if (currency === "USD") {
        symbol = "$";
      } else if (currency === "RON" || currency === "ron") {
        symbol = "RON";
      } else {
        symbol = "€";
      }

      return `${symbol} ${numAmount.toFixed(2)}`;
    } catch {
      return "Invalid Amount";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      const currentDate = new Date();
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }

    return safeFormatDate(dateString, {}, "Invalid Date");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      succeeded: {
        label: t("billingPage.billingHistory.statusLabels.paid"),
        className: styles.statusPaid,
      },
      requires_payment_method: {
        label: t("billingPage.billingHistory.statusLabels.pending"),
        className: styles.statusPending,
      },
      requires_confirmation: {
        label: t("billingPage.billingHistory.statusLabels.confirming"),
        className: styles.statusPending,
      },
      processing: {
        label: t("billingPage.billingHistory.statusLabels.processing"),
        className: styles.statusPending,
      },
      failed: {
        label: t("billingPage.billingHistory.statusLabels.failed"),
        className: styles.statusFailed,
      },
      canceled: {
        label: t("billingPage.billingHistory.statusLabels.canceled"),
        className: styles.statusFailed,
      },
      active: {
        label: t("billingPage.billingHistory.statusLabels.active"),
        className: styles.statusPaid,
      },
      inactive: {
        label: t("billingPage.billingHistory.statusLabels.inactive"),
        className: styles.statusFailed,
      },
      expired: {
        label: t("billingPage.billingHistory.statusLabels.expired"),
        className: styles.statusFailed,
      },
    };
    const config = statusConfig[status] || {
      label: status,
      className: styles.statusDefault,
    };
    return (
      <span className={`${styles.statusBadge} ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getChangeTypeDisplay = (type) => {
    const typeConfig = {
      created: t("billingPage.billingHistory.changeTypes.created"),
      upgraded: t("billingPage.billingHistory.changeTypes.upgraded"),
      downgraded: t("billingPage.billingHistory.changeTypes.downgraded"),
      renewed: t("billingPage.billingHistory.changeTypes.renewed"),
      canceled: t("billingPage.billingHistory.changeTypes.canceled"),
      reactivated: t("billingPage.billingHistory.changeTypes.reactivated"),
      updated: t("billingPage.billingHistory.changeTypes.updated"),
    };
    return typeConfig[type] || type;
  };

  const getServiceDisplay = (record) => {
    if (record.recordType === "subscription") {
      return (
        record.service ||
        `${record.subscriptionPlan} ${t(
          "billingPage.billingHistory.recordTypes.subscription",
        )}`
      );
    }
    return getChangeTypeDisplay(record.changeType);
  };

  const downloadInvoice = async (record) => {
    try {
      const recordId = record._id || record.id;
      setDownloadingId(recordId);
      setDownloadMessage(
        t("billingPage.billingHistory.messages.generatingInvoice"),
      );

      if (!recordId) {
        throw new Error("Invalid record ID");
      }

      if (
        record.status !== "succeeded" &&
        record.status !== "active" &&
        record.status !== "paid"
      ) {
        throw new Error(
          "Invoice can only be generated for successful payments",
        );
      }

      const emailToUse = record.clientEmail || user.email;

      if (record.recordType === "payment") {
        await invoiceService.downloadDomainInvoice(emailToUse, recordId);
      } else {
        await invoiceService.downloadInvoice(emailToUse, recordId);
      }

      setDownloadMessage(
        t("billingPage.billingHistory.messages.invoiceDownloaded"),
      );
      setTimeout(() => setDownloadMessage(""), 3000);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setDownloadMessage(
        t("billingPage.billingHistory.messages.errorDownloading", {
          error: error.message,
        }),
      );
      setTimeout(() => setDownloadMessage(""), 5000);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRetry = () => {
    if (isAuthenticated && user?.email) fetchBillingHistory();
  };

  const userCurrency = useMemo(() => {
    return user?.currency || localStorage.getItem("userCurrency") || "RON";
  }, [user?.currency]);

  const combinedHistory = [
    ...billingHistory.map((record) => ({
      ...record,
      recordType: "subscription",
    })),
    ...paymentHistory.map((record) => ({ ...record, recordType: "payment" })),
  ]
    .filter((record) => {
      const recordCurrency = (record.currency || "RON").toUpperCase();
      const selectedCurrency = userCurrency.toUpperCase();
      return recordCurrency === selectedCurrency;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const totalPages = Math.ceil(combinedHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = combinedHistory.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [combinedHistory.length, userCurrency]);

  if (!isAuthenticated || !user) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
          <p>{t("billingPage.billingHistory.loadingUserData")}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
          <p>{t("billingPage.billingHistory.loadingHistory")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>
            {t("billingPage.billingHistory.errorLoading", { error })}
          </p>
          <Button onClick={handleRetry}>
            {t("billingPage.billingHistory.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            {t("billingPage.billingHistory.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("billingPage.billingHistory.description")}
          </p>
        </div>
        <Button onClick={fetchBillingHistory} style={{ padding: "12px 24px" }}>
          {t("billingPage.billingHistory.refreshHistory")}
        </Button>
      </div>

      {downloadMessage && (
        <div
          className={`${styles.downloadMessage} ${
            downloadMessage.includes("success")
              ? styles.success
              : downloadMessage.includes("Error") ||
                  downloadMessage.includes("Failed")
                ? styles.error
                : styles.info
          }`}
        >
          {downloadMessage}
        </div>
      )}

      {combinedHistory.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon} aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 3h10a2 2 0 0 1 2 2v16l-2-1-2 1-2-1-2 1-2-1-2 1-2-1-2 1V5a2 2 0 0 1 2-2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M8.5 8h7M8.5 12h7M8.5 16H13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p className={styles.emptyStateMessage}>
            {t("billingPage.billingHistory.noHistory")}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className={styles.tableContainer}>
            <table className={styles.billingTable}>
              <thead>
                <tr>
                  <th>{t("billingPage.billingHistory.tableHeaders.planId")}</th>
                  <th>
                    {t("billingPage.billingHistory.tableHeaders.service")}
                  </th>
                  <th>
                    {t("billingPage.billingHistory.tableHeaders.product")}
                  </th>
                  <th>{t("billingPage.billingHistory.tableHeaders.date")}</th>
                  <th>{t("billingPage.billingHistory.tableHeaders.status")}</th>
                  <th>{t("billingPage.billingHistory.tableHeaders.amount")}</th>
                  <th>
                    {t("billingPage.billingHistory.tableHeaders.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedHistory.map((record) => (
                  <tr key={`${record.recordType}-${record._id || record.id}`}>
                    <td className={styles.invoiceId}>
                      {record.projectId}
                      {record.clientEmail === "unknown@example.com" && (
                        <span
                          className={styles.orphanedBadge}
                          title={t("billingPage.billingHistory.orphaned.title")}
                        >
                          {t("billingPage.billingHistory.orphaned.badge")}
                        </span>
                      )}
                    </td>
                    <td className={styles.productName}>
                      {record.recordType === "payment"
                        ? record.service
                        : getServiceDisplay(record)}
                    </td>
                    <td className={styles.productName}>
                      {record.product || t("billingPage.managePlans.na")}
                    </td>
                    <td>
                      {formatDate(record.createdAt || record.lastPaymentDate)}
                    </td>
                    <td>{getStatusBadge(record.status || "unknown")}</td>
                    <td className={styles.amount}>
                      {record.amount && record.amount > 0
                        ? formatAmount(record.amount, userCurrency)
                        : "N/A"}
                    </td>
                    <td className={styles.actions}>
                      <button
                        className={styles.downloadButton}
                        onClick={() => downloadInvoice(record)}
                        disabled={
                          downloadingId === (record._id || record.id) ||
                          (record.status !== "succeeded" &&
                            record.status !== "active" &&
                            record.status !== "paid")
                        }
                        title={
                          record.status !== "succeeded" &&
                          record.status !== "active" &&
                          record.status !== "paid"
                            ? t(
                                "billingPage.billingHistory.actions.invoiceOnlyForSuccessful",
                              )
                            : t(
                                "billingPage.billingHistory.actions.downloadInvoice",
                              )
                        }
                      >
                        {downloadingId === (record._id || record.id) ? (
                          <div className={styles.spinIcon}>⟳</div>
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7,10 12,15 17,10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet Card View */}
          <div className={styles.cardsContainer}>
            {paginatedHistory.map((record) => (
              <div
                key={`${record.recordType}-${record._id || record.id}`}
                className={styles.historyCard}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.recordInfo}>
                    <div className={styles.recordId}>
                      {record.projectId}
                      {record.clientEmail === "unknown@example.com" && (
                        <span
                          className={styles.orphanedBadge}
                          title={t("billingPage.billingHistory.orphaned.title")}
                        >
                          {t("billingPage.billingHistory.orphaned.badge")}
                        </span>
                      )}
                    </div>
                    <div className={styles.recordType}>
                      {record.recordType === "payment"
                        ? t("billingPage.billingHistory.recordTypes.payment")
                        : t(
                            "billingPage.billingHistory.recordTypes.subscription",
                          )}
                    </div>
                  </div>
                  {getStatusBadge(record.status || "unknown")}
                </div>

                <div className={styles.cardBody}>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>
                      {t("billingPage.billingHistory.cardLabels.service")}
                    </span>
                    <span className={styles.cardValue}>
                      {record.recordType === "payment"
                        ? record.service
                        : getServiceDisplay(record)}
                    </span>
                  </div>

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>
                      {t("billingPage.billingHistory.cardLabels.product")}
                    </span>
                    <span className={styles.cardValue}>
                      {record.product || t("billingPage.managePlans.na")}
                    </span>
                  </div>

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>
                      {t("billingPage.billingHistory.cardLabels.date")}
                    </span>
                    <span className={styles.cardValue}>
                      {formatDate(record.createdAt || record.lastPaymentDate)}
                    </span>
                  </div>

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>
                      {t("billingPage.billingHistory.cardLabels.amount")}
                    </span>
                    <span className={styles.cardValue}>
                      {record.amount && record.amount > 0
                        ? formatAmount(record.amount, userCurrency)
                        : t("billingPage.managePlans.na")}
                    </span>
                  </div>
                </div>

                <div className={styles.cardActions}>
                  <button
                    className={styles.downloadButton}
                    onClick={() => downloadInvoice(record)}
                    disabled={
                      downloadingId === (record._id || record.id) ||
                      (record.status !== "succeeded" &&
                        record.status !== "active" &&
                        record.status !== "paid")
                    }
                    title={
                      record.status !== "succeeded" &&
                      record.status !== "active" &&
                      record.status !== "paid"
                        ? t(
                            "billingPage.billingHistory.actions.invoiceOnlyForSuccessful",
                          )
                        : t(
                            "billingPage.billingHistory.actions.downloadInvoice",
                          )
                    }
                  >
                    {downloadingId === (record._id || record.id) ? (
                      <div className={styles.spinIcon}>⟳</div>
                    ) : (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7,10 12,15 17,10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {t(
                          "billingPage.billingHistory.actions.downloadInvoice",
                        )}
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Component */}
          {totalPages > 1 && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                {t("billingPage.billingHistory.pagination.showing", {
                  start: startIndex + 1,
                  end: Math.min(endIndex, combinedHistory.length),
                  total: combinedHistory.length,
                })}
              </div>
              <div className={styles.paginationControls}>
                <button
                  className={`${styles.paginationButton} ${styles.previousButton}`}
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  {t("billingPage.billingHistory.pagination.previous")}
                </button>

                <div className={styles.pageNumbers}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => {
                      const shouldShow =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1);

                      if (!shouldShow) {
                        if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span key={page} className={styles.ellipsis}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={page}
                          className={`${styles.paginationButton} ${
                            styles.pageButton
                          } ${currentPage === page ? styles.activePage : ""}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    },
                  )}
                </div>

                <button
                  className={`${styles.paginationButton} ${styles.nextButton}`}
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  {t("billingPage.billingHistory.pagination.next")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
